
import ManualMachineMap from '@/components/Map/ManualMachineMap.vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import MachineStore from '@/store/modules/machine';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Inject, Vue } from 'vue-property-decorator';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { Guid } from 'guid-typescript';
import { SimplePoint } from '@/flightpath-components/models/SimplePoint';
import { GpsHelper } from '@/flightpath-components/services/GpsHelper';
import { SteeringMode, SteeringModeStatus } from '@/models/ros-application/ExxactMachineStatus';
import { PathHelper } from '@/utils/PathHelper';
import GlobalStore from '@/store/modules/global';
import { SimplePath } from '@/flightpath-components/models/SimplePath';

// Define the component in class-style
@Component({ name: 'ManualMachine', components: { ManualMachineMap } })
export default class ManualMachine extends Vue {
  public machineStore = getModule(MachineStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  @Inject('container')
  private container!: Container;
  panel = [0];
  drawer = false;
  saveDialog = false;
  pauseDialog = false;
  startRecordDialog = false;
  rtkErrorDialog = false;
  stopRecordingDialog = false;
  isPaused = false;
  pathName = '';
  // doing validation manually, so we hide error messages when the path name entry is first shown
  hideErrorMessage = false;
  // not associated with field when recording path, can be set when viewing path
  defaultFieldID = Guid.EMPTY.toString();

  // images are defined like this to make sure webpack includes them, and also to fix vue image scr binding
  twoWheel = require('../assets/2wd.png');
  fourWheel = require('../assets/4wd.png');
  crab = require('../assets/crab.png');

  get pathNameValid() {
    return !!this.pathName;
  }
  get pathNameErrorMessage() {
    const isValid = this.pathNameValid;
    if (this.hideErrorMessage) {
      this.hideErrorMessage = false;
      return null;
    }
    return isValid ? null : [this.$t('field_required')];
  }

  startRecord() {
    this.startRecordDialog = false;

    if (!this.machineStore.rtkState) {
      this.rtkErrorDialog = true;
      return;
    }

    this.machineStore.setRecordPlannedPath(true);
    this.machineStore.setPausePathRecording(false);
  }

  stopRecord() {
    this.stopRecordingDialog = false;
    this.machineStore.setRecordPlannedPath(false);
  }
  activeColor(item: boolean) {
    return item ? '#49cc56' : 'grey';
  }

  steeringImage(item: SteeringMode) {
    if (item == undefined) return this.twoWheel;
    switch (item.mode) {
      case SteeringModeStatus.FrontWheel:
        return this.twoWheel;
      case SteeringModeStatus.FourWheel:
        return this.fourWheel;
      case SteeringModeStatus.Crab:
        return this.crab;
      default:
        return this.twoWheel;
    }
  }

  pauseRecord() {
    this.machineStore.setPausePathRecording(true);
    this.pauseDialog = false;
  }

  showSaveDialog() {
    this.stopRecordingDialog = false;
    if (this.machineStore.recordedPath.length == 0) {
      alert(this.$t('views.manual_driving.alert.no_path'));
      console.log('No path to save');
      this.stopRecord();
    } else {
      this.saveDialog = true;
      this.hideErrorMessage = true;
    }
  }

  showPauseDialog() {
    this.pauseDialog = true;
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();

    this.machineStore.setRecordPlannedPath(false);
  }

  mqttError(error: unknown) {
    console.log(error);
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  savePath() {
    if (!this.pathNameValid) return;
    console.log(`Save path: ${this.pathName}`);
    this.uploadPath();
    this.saveDialog = false;
    this.pathName = '';
    this.stopRecord();
  }

  /**
   * save the current recorded path to the cloud
   */
  async uploadPath() {
    const pathPoints = [] as SimplePoint[];
    const path = this.machineStore.recordedPath;

    const distinctPath = PathHelper.removeDuplicateSuccessivePoints(path);

    // convert path to simple points
    distinctPath.forEach((x) => {
      pathPoints.push({
        Lat: x.Latitude,
        Long: x.Longitude,
        Alt: x.Elevation
      });
    });

    const pathLengthMeters = GpsHelper.findPathLengthInMeters(
      path.map((x) => {
        return new window.google.maps.LatLng({ lat: x.Latitude, lng: x.Longitude });
      })
    );

    const newPath = new SimplePath();
    newPath.PathID = Guid.create().toString();
    newPath.CustomerID = this.globalStore.accountId;
    newPath.Name = this.pathName;
    newPath.Path = pathPoints;
    newPath.WorkAreas = [[]];
    newPath.FieldID = this.defaultFieldID;
    newPath.PathLengthMeters = pathLengthMeters;
    newPath.StrokeOpacity = 0.5;
    newPath.StrokeWeight = 1;
    newPath.LastModifiedUTC = new Date();

    await this.flightPathService?.insertOrUpdatePath(newPath);
  }
}
